import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Grid,
  Rating,
  Stack,
  Typography,
  IconButton,
  Popover,
  Divider,
  Chip,
} from '@mui/material';
import {
  LocationOn,
  MonetizationOn,
  TrendingUp,
  Assessment,
  BusinessCenter,
  ReceiptLong,
  Phone,
  Email,
  ContactPhone,
  NoPhotography as NoPhotographyIcon,
} from '@mui/icons-material';
import { PsiDTO } from '@inversor-hub/types';
import { investmentTypeLabels } from '@/features/investments/constants.ts';
import { formatCurrency, formatPercentage } from '@/utils/misc.ts';
import { useRequestPsiContact } from '@/lib/api/psis.ts';
import { psiServiceLabels } from '@/features/psi/constants.ts';

interface PsiListItemProps {
  psi: PsiDTO;
  children?: React.ReactNode;
  label?: React.ReactNode;
  id?: string;
  sx?: object;
}

const PsiListItem: React.FC<PsiListItemProps> = ({ psi, children, label, id, sx }) => {
  const {
    photoUrl,
    name,
    publicDescription,
    city,
    preferredProvinces,
    avgRating = 0,
    ratingsCount,
    feeAmount,
    specialistInvestmentTypes,
    historicalAvgReturn,
    averageTicket,
    volume,
    totalClosedDeals,
    serviceConfigs,
    pricingModel,
  } = psi;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [contactInfo, setContactInfo] = useState<{ phone?: string; email: string } | null>(null);
  const { mutateAsync: requestContact } = useRequestPsiContact();

  const handleContactClick = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    if (!contactInfo) {
      const info = await requestContact(psi.id);
      setContactInfo(info);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const cardContent = (
    <Card
      id={id}
      variant="outlined"
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        height: '100%',
        transition: 'background-color .2s',
        bgcolor: 'common.white',
        ...sx,
      }}
    >
      <Grid container sx={{ height: '100%' }}>
        <Grid
          item
          xs={12}
          sm={3}
          md={2.5}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'primary.50',
          }}
        >
          <Avatar
            src={photoUrl?.trim() ? photoUrl : undefined}
            alt={name}
            variant="rounded"
            sx={{
              width: { xs: 120, sm: 140, md: 160 },
              height: 'auto',
              borderRadius: 2,
              boxShadow: 1,
              bgcolor: 'grey.200',
              color: 'grey.600',
            }}
          >
            {!photoUrl?.trim() && <NoPhotographyIcon sx={{ fontSize: 60 }} />}
          </Avatar>
        </Grid>

        <Grid item xs={12} sm={9} md={9.5}>
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              height: '100%',
              justifyContent: 'space-between',
              p: { xs: 2, sm: 3 },
            }}
          >
            <Box sx={{ position: 'relative', pr: { md: 12 } }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                  {name}
                </Typography>
                <Rating
                  size="small"
                  readOnly
                  precision={0.5}
                  value={avgRating}
                  getLabelText={(v) => `Valoración global ${v} de 5`}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 0.5, fontWeight: 500 }}
                  aria-label={`Número de valoraciones: ${ratingsCount}`}
                >
                  ({ratingsCount ?? 0})
                </Typography>
                <IconButton
                  onClick={handleContactClick}
                  size="small"
                  sx={{
                    bgcolor: 'primary.50',
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'primary.100' },
                  }}
                  aria-label="Ver información de contacto"
                >
                  <ContactPhone fontSize="small" />
                </IconButton>
              </Stack>

              {/* Main attributes - displayed prominently */}
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  {preferredProvinces.join(', ')}
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {specialistInvestmentTypes
                    .map((type) => investmentTypeLabels[type] || type)
                    .join(', ')}
                </Typography>
              </Box>

              <Typography variant="body2" color={'text.secondary'} sx={{ minHeight: '2.5rem' }}>
                {publicDescription}
              </Typography>

              <Box
                sx={{
                  position: { md: 'absolute', xs: 'static' },
                  top: 0,
                  right: 0,
                  mt: { xs: 1, md: 0 },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {children}
                  {label && (
                    <Stack direction="column" spacing={1} alignItems="flex-end">
                      {label}
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Box>

            <Box sx={{ mt: 'auto' }}>
              {/* Services Display */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Servicios ofrecidos:
                </Typography>
                <ServicesDisplay serviceConfigs={serviceConfigs} />
              </Box>

              <Grid container spacing={2}>
                {/* Contact and location info */}
                <Info icon={<LocationOn />} label="Ciudad" value={city} />

                {/* Pricing information based on model */}
                <PricingInfo
                  pricingModel={pricingModel}
                  feeAmount={feeAmount}
                  serviceConfigs={serviceConfigs}
                />

                <Info
                  icon={<TrendingUp />}
                  label="Rentabilidad histórica"
                  value={formatPercentage(historicalAvgReturn)}
                />
                <Info
                  icon={<ReceiptLong />}
                  label="Ticket promedio"
                  value={formatCurrency(averageTicket)}
                />
                <Info
                  icon={<BusinessCenter />}
                  label="Volumen total"
                  value={formatCurrency(volume)}
                />
                <Info
                  icon={<Assessment />}
                  label="Operaciones cerradas"
                  value={totalClosedDeals?.toString()}
                />
              </Grid>
            </Box>
          </CardContent>
        </Grid>
      </Grid>
    </Card>
  );

  return (
    <Grid item xs={12}>
      {cardContent}
      {/* Contact Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Contactar
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <Stack spacing={1}>
            {contactInfo?.phone && (
              <ContactOption
                icon={<Phone fontSize="small" />}
                label="Teléfono"
                value={contactInfo.phone}
                onClick={handlePopoverClose}
              />
            )}
            {contactInfo?.email && (
              <ContactOption
                icon={<Email fontSize="small" />}
                label="Email"
                value={contactInfo.email}
                onClick={handlePopoverClose}
              />
            )}
          </Stack>
        </Box>
      </Popover>
    </Grid>
  );
};

interface InfoProps {
  icon: React.ReactNode;
  label: string;
  value?: string | number | null;
}

const Info: React.FC<InfoProps> = ({ icon, label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box color="primary.main">{icon}</Box>
      <Typography variant="subtitle2" fontWeight={600}>
        {label}:
      </Typography>
      <Typography variant="body2" color="primary.main">
        {value || 'No especificado'}
      </Typography>
    </Stack>
  </Grid>
);

interface ContactOptionProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  onClick: () => void;
}

const ContactOption: React.FC<ContactOptionProps> = ({ icon, label, value, onClick }) => {
  const handleClick = () => {
    if (label === 'Teléfono') {
      window.open(`tel:${value}`, '_self');
    } else if (label === 'Email') {
      window.open(`mailto:${value}`, '_self');
    }
    onClick();
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        cursor: 'pointer',
        '&:hover': { bgcolor: 'grey.100' },
        p: 1,
        borderRadius: 1,
        transition: 'background-color 0.2s',
      }}
      onClick={handleClick}
    >
      <Box color="primary.main">{icon}</Box>
      <Typography variant="body2" color="text.primary">
        <strong>{label}:</strong> {value}
      </Typography>
    </Stack>
  );
};

const ServicesDisplay: React.FC<{
  serviceConfigs: PsiDTO['serviceConfigs'];
}> = ({ serviceConfigs }) => {
  return (
    <Stack direction="row" flexWrap="wrap" spacing={0.5}>
      {serviceConfigs.map((config) => {
        const label = psiServiceLabels[config.service];
        return (
          <Chip
            key={config.service}
            label={label}
            size="small"
            sx={{
              bgcolor: 'primary.50',
              color: 'primary.main',
              borderRadius: 1,
              mb: 0.5,
              fontSize: '0.75rem',
              height: 24,
            }}
          />
        );
      })}
    </Stack>
  );
};

// Pricing information component handling different pricing models
const PricingInfo: React.FC<{
  pricingModel: PsiDTO['pricingModel'];
  feeAmount: number;
  serviceConfigs: PsiDTO['serviceConfigs'];
}> = ({ pricingModel, feeAmount, serviceConfigs }) => {
  const [pricingAnchorEl, setPricingAnchorEl] = useState<null | HTMLElement>(null);

  const handlePricingHover = (event: React.MouseEvent<HTMLElement>) => {
    if (pricingModel === 'PER_SERVICE') {
      setPricingAnchorEl(event.currentTarget);
    }
  };

  const handlePricingClose = () => {
    setPricingAnchorEl(null);
  };

  const pricingOpen = Boolean(pricingAnchorEl);

  if (pricingModel === 'PACKAGE') {
    return (
      <Info
        icon={<MonetizationOn />}
        label="Honorarios (paquete)"
        value={formatCurrency(feeAmount)}
      />
    );
  }

  if (pricingModel === 'PER_SERVICE') {
    return (
      <>
        <Grid item xs={12} sm={6} md={4}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box color="primary.main">
              <MonetizationOn />
            </Box>
            <Typography variant="subtitle2" fontWeight={600}>
              Honorarios:
            </Typography>
            <Typography
              variant="body2"
              color="primary.main"
              sx={{
                cursor: 'pointer',
                textDecoration: 'underline',
                textDecorationStyle: 'dotted',
                '&:hover': {
                  textDecoration: 'underline',
                  textDecorationStyle: 'solid',
                },
              }}
              onMouseEnter={handlePricingHover}
              onMouseLeave={handlePricingClose}
            >
              Por servicio
            </Typography>
          </Stack>
        </Grid>

        {/* Per-service pricing popover */}
        <Popover
          open={pricingOpen}
          anchorEl={pricingAnchorEl}
          onClose={handlePricingClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          disableRestoreFocus
          sx={{
            pointerEvents: 'none',
          }}
          slotProps={{
            paper: {
              sx: {
                pointerEvents: 'auto',
              },
            },
          }}
        >
          <Box sx={{ p: 2, minWidth: 300 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Tarifas por servicio
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Stack spacing={1}>
              {serviceConfigs.map((config) => (
                <Stack
                  key={config.service}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.primary">
                    {psiServiceLabels[config.service]}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="primary.main">
                    {config.fee ? formatCurrency(config.fee) : 'Consultar'}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Popover>
      </>
    );
  }

  return null;
};

export default PsiListItem;
